import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import LayoutSelector from "../components/LayoutSelector";
import { useLayout } from "../context/layoutContext";
import { loadSession, useSettingsString } from "../context/trackerContext";
import SettingStringsJSON from "../data/setting-strings.json";
import useDebounce from "../hooks/useDebounce";
import { isValidLayout } from "../utils/layout-validation";
import { writeJSON, writeString } from "../utils/safe-storage";

const baseURL = process.env.PUBLIC_URL;
const GENERATOR_VERSION = process.env.REACT_APP_GENERATOR_VERSION;

const PRESETS = SettingStringsJSON.presets || [];
const CURRENT_ACTIVE_VERSION = SettingStringsJSON.currentActiveVersion || "9.0.0";
const GENERATOR_VERSIONS = SettingStringsJSON.supportedVersions || ["9.0.0"];

const DEV_BRANCHES = SettingStringsJSON.supportedDevBranches || [];
const SELECTABLE_VERSIONS = [...GENERATOR_VERSIONS, ...DEV_BRANCHES.map((branch) => branch.value)];

/**
 * Parses and validates the layout a session snapshot stores as a JSON string.
 * @param {object|null} session - The saved session snapshot.
 * @returns {object|null} The parsed layout, or null when missing/invalid.
 */
function getSessionLayout(session) {
  if (!session || typeof session.layout !== "string") { return null; }
  try {
    const layout = JSON.parse(session.layout);
    return isValidLayout(layout) ? layout : null;
  } catch (err) {
    console.warn("Saved session has an unreadable layout:", err);
    return null;
  }
}

const TrackerLauncher = () => {
  const [checks, setChecks] = useState(false);
  const { state: layout } = useLayout();

  const layoutSize = useMemo(() => {
    const {
      layoutConfig: { width, height },
    } = layout;

    // Default padding to layouts
    let widthPadding = 0;
    let heightPadding = 25;
    if (checks) {
      widthPadding = 285;
      heightPadding = 25;
    }

    return {
      width: width + widthPadding,
      height: height + heightPadding,
    };
  }, [checks, layout]);

  const {
    setString: setSettingsStringCache,
    settings_string: cachedSettingsString,
    setVersion: setGeneratorVersionCache,
    generator_version: cachedGeneratorVersion,
  } = useSettingsString();

  const [settingsString, setSettingsString] = useState(
    () => cachedSettingsString || ""
  );
  const debouncedString = useDebounce(settingsString, 300);

  // Mirror the field into localStorage, but only once the user has actually edited it.
  const settingsTouched = useRef(false);
  useEffect(() => {
    if (!settingsTouched.current) { return; }
    setSettingsStringCache(debouncedString);
  }, [debouncedString, setSettingsStringCache]);

  const [generatorVersion, setGeneratorVersion] = useState(
    () => cachedGeneratorVersion || CURRENT_ACTIVE_VERSION
  );
  const [isCustomVersion, setIsCustomVersion] = useState(
    () => {
      const version = cachedGeneratorVersion || CURRENT_ACTIVE_VERSION;
      return version && !SELECTABLE_VERSIONS.includes(version);
    }
  );
  const debouncedVersion = useDebounce(generatorVersion, 300);

  // Auto-detect if version is custom when it changes
  useEffect(() => {
    if (generatorVersion) {
      setIsCustomVersion(!SELECTABLE_VERSIONS.includes(generatorVersion));
    }
  }, [generatorVersion]);

  useEffect(() => {
    setGeneratorVersionCache(debouncedVersion);
  }, [debouncedVersion, setGeneratorVersionCache]);

  const missingSettingsString = checks && !settingsString.trim();
  const [launchAttempted, setLaunchAttempted] = useState(false);

  const launchTracker = useCallback(() => {
    if (missingSettingsString) {
      setLaunchAttempted(true);
      return;
    }

    let url = `${baseURL}/tracker`;
    if (checks) { url = `${baseURL}/tracker/checks`; }

    // Launch with exactly what the launcher currently displays, in case a
    // prior resumeSession overwrote the cached config in localStorage.
    writeJSON("layout", layout);
    writeString("settings_string", settingsString);
    writeString("generator_version", generatorVersion);

    const { width, height } = layoutSize;

    window.open(
      url,
      "HashFrog Tracker",
      `toolbar=0,location=0,status=0,menubar=0,scrollbars=0,resizable=0,width=${width},height=${height}`
    );
  }, [missingSettingsString, checks, layout, settingsString, generatorVersion, layoutSize]);

  // Track whether a saved session exists so the Resume button reacts when one
  // is created in a popup window; refresh on focus when returning to the launcher.
  const [savedSession, setSavedSession] = useState(loadSession);
  useEffect(() => {
    const refresh = () => setSavedSession(loadSession());
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  // A session whose layout is missing or damaged cannot be resumed.
  const canResume = useMemo(() => !!getSessionLayout(savedSession), [savedSession]);

  const resumeSession = useCallback(() => {
    // Re-read fresh: another window may have saved a newer session since the
    // launcher last rendered, leaving the render-time `savedSession` stale.
    const session = loadSession();
    const sessionLayout = getSessionLayout(session);
    if (!session || !sessionLayout) {
      // Sync the button state so a now-unusable session disables Resume.
      setSavedSession(session);
      return;
    }

    // Force the resumed window to reproduce the saved session's config.
    writeString("layout", session.layout);
    writeString("settings_string", session.settings_string || "");
    writeString("generator_version", session.generator_version || "");

    const resumeChecks = !!session.checksEnabled;
    let url = resumeChecks ? `${baseURL}/tracker/checks` : `${baseURL}/tracker`;
    url += "?resume=1";

    const {
      layoutConfig: { width, height },
    } = sessionLayout;
    const windowWidth = width + (resumeChecks ? 285 : 0);
    const windowHeight = height + 25;

    window.open(
      url,
      "HashFrog Tracker",
      `toolbar=0,location=0,status=0,menubar=0,scrollbars=0,resizable=0,width=${windowWidth},height=${windowHeight}`
    );
  }, []);

  const updateString = (preset) => {
    settingsTouched.current = true;

    if (preset.settingsString) {
      setSettingsString(preset.settingsString);
    } else {
      // This should never happen. if it does, a preset has no settingString.
      setSettingsString("UNKNOWN_SETTINGS_STRING");
    }

    // Use the mapped generator version. If not, use .env, if not, use the current active hardcoded version. (9.0.0 as of 1/25/2026)
    if (preset.generatorVersion) {
      setGeneratorVersion(preset.generatorVersion);
    } else if (GENERATOR_VERSION) {
      setGeneratorVersion(GENERATOR_VERSION);
    } else {
      setGeneratorVersion(CURRENT_ACTIVE_VERSION);
    }
  };

  // Check if current settings match any preset
  const activePreset = useMemo(() => {
    return PRESETS.find(
      (preset) => preset.settingsString === settingsString
    );
  }, [settingsString]);

  return (
    <Fragment>
      {/* Main Launcher Card */}
      <Card className="bg-dark border-secondary text-white mb-4">
        <Card.Body>
          <h5
            className="card-title text-uppercase fw-bold text-white mb-4"
            style={{ letterSpacing: "0.05em" }}
          >
            Tracker Settings
          </h5>

          <div className="d-grid mb-3">
            <Button
              type="button"
              variant="success"
              size="lg"
              onClick={launchTracker}
              className="fw-semibold"
            >
              🚀 Launch New Tracker
            </Button>
          </div>

          <div
            className="d-grid mb-4"
            title={savedSession && !canResume ? "The saved session's layout is damaged and cannot be resumed." : undefined}
          >
            <Button
              type="button"
              variant="outline-light"
              onClick={resumeSession}
              disabled={!canResume}
            >
              ↻ Resume Session
            </Button>
            {savedSession && !canResume && (
              <p className="small text-warning mt-1 mb-0">
                The saved session&apos;s layout is damaged, so it cannot be resumed.
              </p>
            )}
          </div>

          <Form.Check
            type="switch"
            id="checks"
            label="Enable check tracking"
            checked={checks}
            onChange={() => setChecks((prev) => !prev)}
            className="mb-3 text-light"
          />

          {checks && (
            <div className="border-top border-secondary pt-3 mt-3">
              <p className="small text-secondary mb-2">
                Configure logic settings for check tracking
              </p>
              {activePreset ? (
                <p className="mb-3">
                  <span className="badge bg-success d-inline-flex align-items-center gap-1">
                    ⭐ Using {activePreset.label} Preset
                  </span>
                </p>
              ) : settingsString && (
                <p className="mb-3">
                  <span className="badge bg-warning text-dark d-inline-flex align-items-center gap-1">
                    ⚙️ Using Custom Settings String
                  </span>
                </p>
              )}

              <div className="row g-3 mb-3">
                <div className="col-4">
                  <Form.Label
                    htmlFor="generator_version"
                    className="text-secondary"
                  >
                    Generator Version
                  </Form.Label>
                  {isCustomVersion ? (
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        id="generator_version"
                        name="generator_version"
                        placeholder="Enter custom version"
                        value={generatorVersion}
                        onChange={({ target: { value } }) =>
                          setGeneratorVersion(value)
                        }
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setIsCustomVersion(false);
                          setGeneratorVersion(CURRENT_ACTIVE_VERSION);
                        }}
                      >
                        ×
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Select
                      size="sm"
                      id="generator_version"
                      name="generator_version"
                      value={generatorVersion}
                      onChange={({ target: { value } }) => {
                        if (value === "__other__") {
                          setIsCustomVersion(true);
                          setGeneratorVersion("");
                        } else {
                          setGeneratorVersion(value);
                        }
                      }}
                    >
                      <option value="">Select version</option>
                      <optgroup label="Releases">
                        {GENERATOR_VERSIONS.map((version) => (
                          <option key={version} value={version}>
                            {version}
                          </option>
                        ))}
                      </optgroup>
                      {DEV_BRANCHES.length > 0 && (
                        <optgroup label="Dev branches (newest logic)">
                          {DEV_BRANCHES.map((branch) => (
                            <option key={branch.value} value={branch.value}>
                              {branch.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value="__other__">Other...</option>
                    </Form.Select>
                  )}
                </div>
                <div className="col-8">
                  <Form.Label htmlFor="setting_string" className="text-secondary">
                    Settings String
                  </Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="text"
                      id="setting_string"
                      name="setting_string"
                      placeholder="Paste settings string here"
                      value={settingsString}
                      onChange={({ target: { value } }) => {
                        settingsTouched.current = true;
                        setSettingsString(value);
                      }}
                    />
                  </InputGroup>
                  {launchAttempted && missingSettingsString && (
                    <div className="text-danger small mt-1">
                      Enter a settings string or pick a preset to launch with check tracking.
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Form.Label className="text-secondary mb-0 small">
                  Quick Presets:
                </Form.Label>
                <DropdownButton
                  id="presets-dropdown"
                  title="Select Preset"
                  variant="outline-light"
                  size="sm"
                >
                  {PRESETS.map((preset) => (
                    <Dropdown.Item
                      key={preset.value}
                      onClick={() => updateString(preset)}
                    >
                      {preset.label}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>

              <Alert variant="info" className="mt-3 mb-0 py-2 small">
                Your seed page shows the version: a number like <code>9.1.0</code> for releases or a branch name
                like <code>Dev-Rob v9.0.2-17</code> for dev seeds. Pick the branch from the list or select
                &ldquo;Other...&rdquo;. A dev branch always loads its newest logic. An older seed may not match it exactly.
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Layout Configuration Card */}
      <Card className="bg-dark border-secondary text-white mb-4">
        <Card.Body>
          <h5
            className="card-title text-uppercase fw-bold text-white mb-3"
            style={{ letterSpacing: "0.05em" }}
          >
            Layout Configuration
          </h5>
          <LayoutSelector />
        </Card.Body>
      </Card>

      {/* Notes Card */}
      <Card className="bg-dark border-secondary text-white">
        <Card.Body>
          <h6
            className="text-uppercase fw-bold text-white mb-3"
            style={{ letterSpacing: "0.05em" }}
          >
            Notes
          </h6>
          <p className="small text-warning mb-2">
            * Check tracking requires a compatible layout configuration.
          </p>
          <ul className="small text-secondary mb-0 ps-3">
            <li className="mb-1">
              The logic assumes: no shuffled entrances, owl drops, warp song
              destinations, or spawns; and vanilla (default) ocarina melodies.
            </li>
            <li className="mb-1">
              Closed Forest does not work for the reasons above.
            </li>
            <li>
              The logic assumes that the initial value for a counter is zero.
              Click the counter to update it if not.
            </li>
            <li>
              Advanced logic is not yet supported.
            </li>
            <li>
              Checks with the same name plus a number prefix are grouped together
              if they have the same access requirements, with the total number of
              checks shown in parentheses, e.g. Guard House Child Pot (44).
            </li>
          </ul>
        </Card.Body>
      </Card>
    </Fragment>
  );
};

export default TrackerLauncher;
